/**
 * UserRentals.js
 * Component for displaying and managing user rentals
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Spinner,
  Table,
  Badge,
  ButtonGroup,
  Button
} from 'react-bootstrap';
import { rentalService } from '../../services/api';

const UserRentals = ({ userId }) => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('active'); // 'active' or 'completed'
  
  // Modal per le note
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState('');

  useEffect(() => {
    if (userId) {
      loadRentals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, filter]);

  const loadRentals = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use rentals query with filter
      const filterInput = {
        userId: userId,
        isActive: filter === 'active' ? true : false
      };
      
      const response = await rentalService.getAll(1, 100, filterInput);

      if (response.success) {
        const allRentals = response.data.items || [];
        setRentals(allRentals);
      } else {
        setError('Errore nel caricamento dei noleggi');
      }
    } catch (err) {
      setError('Errore nel caricamento dei noleggi');
      console.error('Error loading rentals:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRentalStatus = (rental) => {
    if (rental.actualReturnDate) {
      return <Badge bg="success">Restituito</Badge>;
    }
    
    if (rental.expectedReturnDate) {
      const expectedDate = new Date(rental.expectedReturnDate);
      const today = new Date();
      
      if (expectedDate < today) {
        return <Badge bg="danger">In ritardo</Badge>;
      }
    }
    
    return <Badge bg="primary">In corso</Badge>;
  };

  const handleShowNotes = (notes) => {
    setSelectedNotes(notes);
    setShowNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedNotes('');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-rentals">
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Filter buttons */}
      <div className="mb-4">
        <ButtonGroup>
          <Button
            variant={filter === 'active' ? 'primary' : 'outline-primary'}
            onClick={() => setFilter('active')}
          >
            <i className="fas fa-clock me-2"></i>
            Noleggi in Corso
          </Button>
          <Button
            variant={filter === 'completed' ? 'primary' : 'outline-primary'}
            onClick={() => setFilter('completed')}
          >
            <i className="fas fa-check-circle me-2"></i>
            Noleggi Conclusi
          </Button>
        </ButtonGroup>
      </div>

      {rentals.length === 0 ? (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          Nessun noleggio {filter === 'active' ? 'in corso' : 'concluso'} trovato.
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>Articolo</th>
              <th>Quantità</th>
              <th>Data Inizio</th>
              <th>Data Prevista Restituzione</th>
              <th>Data Restituzione</th>
              <th>Stato</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rentals.map((rental) => (
              <tr key={rental.id}>
                <td>
                  <strong>{rental.rentableItem?.name || 'N/D'}</strong>
                  {rental.rentableItem?.description && (
                    <div className="text-muted small">
                      {rental.rentableItem.description}
                    </div>
                  )}
                </td>
                <td>{rental.quantity}</td>
                <td>{formatDate(rental.startDate)}</td>
                <td>{formatDate(rental.expectedReturnDate)}</td>
                <td>
                  {rental.actualReturnDate ? (
                    formatDate(rental.actualReturnDate)
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{getRentalStatus(rental)}</td>
                <td>
                  {rental.notes ? (
                    <i 
                      className="fas fa-info-circle text-info" 
                      style={{ cursor: 'pointer' }}
                      title={rental.notes.length > 32 ? rental.notes.substring(0, 32) + '...' : rental.notes}
                      onClick={() => handleShowNotes(rental.notes)}
                    ></i>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Summary */}
      {rentals.length > 0 && (
        <div className="mt-3">
          <small className="text-muted">
            Totale: {rentals.length} {filter === 'active' ? 'noleggi in corso' : 'noleggi conclusi'}
          </small>
        </div>
      )}

      {/* Modal per visualizzare le note */}
      {showNotesModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseNotesModal}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-sticky-note me-2"></i>
                  Note del noleggio
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseNotesModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedNotes}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseNotesModal}
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRentals;
